import { useState, useEffect } from 'react';
import { UserPlus, Loader2, Search, User, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { getRoleDisplayName } from '../utils/roleUtils';

/**
 * Who on the client side owns a config item (ledger L21): the person who
 * answers its questions, with an optional due date and note. Opened from the
 * discovery grid's cell drawer; the list is the client company's users from
 * `companies/:companyId/users`.
 */

/** The few fields the dialog needs; a grid block or a full config item both fit. */
export interface AssignableItem {
  id: string;
  name: string;
  assignedTo?: string;
  assignedToName?: string;
}

interface ConfigItemAssignmentDialogProps {
  item: AssignableItem | null;
  companyId: string;
  accessToken: string;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

interface CompanyUser {
  id: string;
  email: string;
  name?: string;
  isCompanyAdmin?: boolean;
  status?: string;
}

export function ConfigItemAssignmentDialog({
  item,
  companyId,
  accessToken,
  open,
  onClose,
  onUpdate,
}: ConfigItemAssignmentDialogProps) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [assignmentNote, setAssignmentNote] = useState('');

  useEffect(() => {
    if (open) {
      loadCompanyUsers();
    }
  }, [open, companyId]);

  const loadCompanyUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error(`Could not load the client's team (${response.status})`);
      const data = await response.json();
      // The route returns the client company's users only (no consultants), already sanitized.
      setUsers((data.users || []).filter((u: CompanyUser) => u.status !== 'inactive'));
    } catch (error: any) {
      console.error('Error loading company users:', error);
      toast.error(error?.message || "Could not load the client's team");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (user: CompanyUser) => {
    if (!item) return;

    try {
      setAssigning(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${item.id}/assign`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.id,
            userName: user.name || user.email,
            dueDate: dueDate || null,
            assignmentNote: assignmentNote || null,
          }),
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || `Could not assign the item (${response.status})`);
      }
      toast.success(`Assigned to ${user.name || user.email}`);
      setSelectedUser(null);
      setDueDate('');
      setAssignmentNote('');
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Error assigning item:', error);
      toast.error(error?.message || 'Could not assign the item');
    } finally {
      setAssigning(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.name && user.name.toLowerCase().includes(searchLower))
    );
  });

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="size-5" />
            Who owns "{item.name}" on the client side?
          </DialogTitle>
          <DialogDescription>
            That person answers this item's questions and gets the reminders. Everyone on the client team still sees the whole grid.
          </DialogDescription>
        </DialogHeader>

        <Separator />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search the client team by name or email"
            className="pl-10"
          />
        </div>

        {/* Current assignment */}
        {item.assignedTo && !selectedUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="size-4 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Currently assigned to:</p>
                <p className="text-sm text-blue-700">{item.assignedToName || item.assignedTo}</p>
              </div>
            </div>
            <Badge variant="secondary">Assigned</Badge>
          </div>
        )}

        {/* Step 1: Select User */}
        {!selectedUser && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-6 animate-spin text-gray-400" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>{searchTerm ? `Nobody on the client team matches "${searchTerm}"` : 'Nobody is on the client team yet. Invite someone from the client page first.'}</p>
              </div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="border rounded-lg p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="size-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{user.name || user.email}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      {user.isCompanyAdmin && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {getRoleDisplayName('companyAdmin')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => setSelectedUser(user)}
                    variant={item.assignedTo === user.id ? 'secondary' : 'default'}
                  >
                    {item.assignedTo === user.id ? 'Reassign' : 'Select'}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Step 2: Configure Assignment Details */}
        {selectedUser && (
          <div className="space-y-4">
            {/* Selected user */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="size-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Assigning to:</p>
                  <p className="text-sm text-blue-700">{selectedUser.name || selectedUser.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUser(null)}
              >
                Change
              </Button>
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label htmlFor="dueDate" className="flex items-center gap-2">
                <Calendar className="size-4" />
                Due Date (optional)
              </Label>
              <Input
                id="dueDate"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                The assignee will receive a reminder email at 8am Eastern the day before it's due.
              </p>
            </div>

            {/* Assignment Note */}
            <div className="space-y-2">
              <Label htmlFor="assignmentNote">
                Note (optional)
              </Label>
              <Textarea
                id="assignmentNote"
                value={assignmentNote}
                onChange={(e) => setAssignmentNote(e.target.value)}
                placeholder="e.g., Please review the vendor options by Friday..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This note will be shown to the assignee with the action item.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedUser(null);
                  setDueDate('');
                  setAssignmentNote('');
                }}
                disabled={assigning}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleAssign(selectedUser)}
                disabled={assigning}
              >
                {assigning ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  'Assign Item'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Info - only show when not in assignment details */}
        {!selectedUser && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
            <p>
              <strong>Tip:</strong> Assign items to the person most knowledgeable about that area. 
              For example, assign HR-related items to the HR Manager.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}